import { useQuery } from '@apollo/client';
import React from 'react';
import { useTranslation } from 'react-i18next';

import SharePointTreeBrowser from './SharePointTreeBrowser.tsx';
import { slimRenderOption } from './slimRenderOption.tsx';
import { SourceSyncStatusLine } from './SourceSyncStatusLine.tsx';
import SelectField, { Option } from '../../../../core/components/Fields/SelectField.tsx';
import FormField from '../../../../core/components/FormField.tsx';
import Switch from '../../../../core/components/Switch.tsx';
import classnames from '../../../../core/utils/classnames.tsx';
import { useControllableState } from '../../../../core/utils/useControllableState.tsx';
import {
    GET_SHAREPOINT_INTEGRATIONS,
    GET_SHAREPOINT_SITES,
    GET_SHAREPOINT_DRIVES,
} from '../../../../customer/queries.ts';
import {
    SharePointSelectedItem,
    SharePointToolProviderConfig,
    SHAREPOINT_TOOL_PROVIDER_DEFAULT,
} from '../config/ConfigSteps.tsx';

export const SharePointProviderCard = ({
    isChecked,
    disabled,
    onIsCheckedChange,
    config: propsConfig,
    onConfigChange: propsOnConfigChange,
    className,
    ...props
}: React.ComponentPropsWithRef<'div'> & {
    isChecked: boolean;
    disabled?: boolean;
    onIsCheckedChange: (checked: boolean) => void;
    config?: SharePointToolProviderConfig | null;
    onConfigChange?: (config: SharePointToolProviderConfig | null) => void;
}) => {
    const { t } = useTranslation('assistants', { keyPrefix: 'agentConfig' });

    const { data: integrationsData, loading: integrationsLoading } = useQuery(GET_SHAREPOINT_INTEGRATIONS, {
        skip: !isChecked,
    });
    const integrations = integrationsData?.sharepointIntegrations ?? [];
    const integrationOptions = integrations.map((i: { id: string; name: string }) => ({
        value: i.id,
        label: i.name,
    }));

    const [config, setConfig] = useControllableState(
        { ...SHAREPOINT_TOOL_PROVIDER_DEFAULT },
        propsConfig,
        propsOnConfigChange
    );

    const { data: sitesData, loading: sitesLoading } = useQuery(GET_SHAREPOINT_SITES, {
        skip: !config?.integrationId,
        variables: { integrationId: config?.integrationId || '', search: '*' },
    });
    const siteOptions = (sitesData?.sharepointSites ?? []).map((s: { id: string; displayName: string }) => ({
        value: s.id,
        label: s.displayName,
    }));

    const { data: drivesData, loading: drivesLoading } = useQuery(GET_SHAREPOINT_DRIVES, {
        skip: !config?.integrationId || !config?.siteId,
        variables: { integrationId: config?.integrationId || '', siteId: config?.siteId || '' },
    });
    const driveOptions = (drivesData?.sharepointDrives ?? []).map((d: { id: string; name: string }) => ({
        value: d.id,
        label: d.name,
    }));

    const handleToggle = (checked: boolean) => {
        onIsCheckedChange(checked);
        setConfig(checked ? { ...SHAREPOINT_TOOL_PROVIDER_DEFAULT } : null);
    };

    // the display names travel with the ids so project members without browsing rights can read the setup
    const labelOf = (options: Option[], value: string) => options.find((option) => option.value === value)?.label;

    const handleIntegrationChange = (integrationId: string) => {
        setConfig({
            ...SHAREPOINT_TOOL_PROVIDER_DEFAULT,
            integrationId,
            integrationName: labelOf(integrationOptions, integrationId),
        });
    };

    const handleSiteChange = (siteId: string) => {
        setConfig({
            ...SHAREPOINT_TOOL_PROVIDER_DEFAULT,
            integrationId: config?.integrationId || '',
            integrationName: config?.integrationName,
            siteId,
            siteName: labelOf(siteOptions, siteId),
        });
    };

    const handleDriveChange = (driveId: string) => {
        setConfig({
            ...SHAREPOINT_TOOL_PROVIDER_DEFAULT,
            integrationId: config?.integrationId || '',
            integrationName: config?.integrationName,
            siteId: config?.siteId || '',
            siteName: config?.siteName,
            driveId,
            driveName: labelOf(driveOptions, driveId),
        });
    };

    const handleSelectedItemsChange = (selectedItems: SharePointSelectedItem[]) => {
        setConfig({ ...config!, selectedItems });
    };

    return (
        <div
            className={classnames(
                'rounded-lg flex flex-col gap-2 p-2 border border-line-weak border-solid bg-surface-weak transition-colors',
                disabled && 'opacity-50',
                className
            )}
            {...props}
        >
            <label className="flex flex-col cursor-pointer hover:bg-surface-medium group transition-colors rounded-lg">
                <div className="flex justify-between items-center p-2 pb-0">
                    <div className="flex gap-2 items-center">
                        <img
                            src="/images/external_logos/sharepoint.png"
                            alt={t('toolProviders.sharepoint.name')}
                            className="w-auto h-5 overflow-hidden"
                        />
                        <h3 className="text-sm font-medium text-content-strong">
                            {t('toolProviders.sharepoint.name')}
                        </h3>
                    </div>
                    <Switch value={isChecked} onValueChange={handleToggle} disabled={disabled} />
                </div>
                <div className="text-sm text-content-medium px-2 pb-2">{t('toolProviders.sharepoint.description')}</div>
            </label>

            {isChecked && config && (
                <div className="flex flex-col gap-4 bg-surface-medium rounded-lg p-2 px-3">
                    <SourceSyncStatusLine config={config} />
                    <FormField label={t('toolProviders.sharepoint.integrationLabel')}>
                        <SelectField
                            options={integrationOptions}
                            placeholder={t('toolProviders.sharepoint.integrationPlaceholder')}
                            loadingPlaceholder={t('toolProviders.sharepoint.loading')}
                            isLoading={integrationsLoading}
                            value={config.integrationId}
                            onValueChange={handleIntegrationChange}
                            renderOption={slimRenderOption}
                            modal
                        />
                    </FormField>

                    {config.integrationId && (
                        <FormField label={t('toolProviders.sharepoint.siteLabel')}>
                            <SelectField
                                options={siteOptions}
                                placeholder={t('toolProviders.sharepoint.sitePlaceholder')}
                                loadingPlaceholder={t('toolProviders.sharepoint.loading')}
                                isLoading={sitesLoading}
                                value={config.siteId}
                                onValueChange={handleSiteChange}
                                renderOption={slimRenderOption}
                                modal
                            />
                        </FormField>
                    )}

                    {config.siteId && (
                        <FormField label={t('toolProviders.sharepoint.driveLabel')}>
                            <SelectField
                                options={driveOptions}
                                placeholder={t('toolProviders.sharepoint.drivePlaceholder')}
                                loadingPlaceholder={t('toolProviders.sharepoint.loading')}
                                isLoading={drivesLoading}
                                value={config.driveId}
                                onValueChange={handleDriveChange}
                                renderOption={slimRenderOption}
                                modal
                            />
                        </FormField>
                    )}

                    {config.driveId && (
                        <FormField label={t('toolProviders.sharepoint.knowledgeSourcesLabel')} required>
                            <SharePointTreeBrowser
                                integrationId={config.integrationId}
                                driveId={config.driveId}
                                selectedItems={config.selectedItems ?? []}
                                onSelectedItemsChange={handleSelectedItemsChange}
                            />
                        </FormField>
                    )}
                </div>
            )}
        </div>
    );
};
